import React, { useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { getRoundOverviewFields } from '../../../utils/fieldConfigs.ts'
import { AccountRecord } from '../../../../../state_manager/data_models/account_record.ts'
import { useCurrencies } from '../../../../currencies/useCurrencies.ts'
import { AccountRecordFields } from '../../../../../state_manager/data_models/account_record.ts'
import { RoundEntryFields } from '../../../../../state_manager/data_models/round_entry.ts'
import { updateCompanyRecord } from '../../../../../state_manager/state_update_actions/companies/index.ts'
import { selectCompanyById } from '../../../../../state_manager/state_update_actions/companies/selectors.ts'
import { addRound } from '../../../../../state_manager/state_update_actions/rounds/index.ts'
import { useTabManagerContext } from '../../../../../core/app-layout/lib/TabManagerContext.tsx'

export function RoundOverviewSection({
  company,
  formData,
  handleFieldChange,
  onPatchSuccess,
  showTitle = true,
  cardProps = {},
  isReadOnly = false,
  recordId = null
}: {
  company?: AccountRecord | null
  formData?: Record<string, any>
  handleFieldChange?: (field: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  showTitle?: boolean
  cardProps?: Record<string, any>
  isReadOnly?: boolean
  recordId?: string | null
}) {
  const dispatch = useDispatch()
  const { getCurrencyById } = useCurrencies()
  const { openFormInTab } = useTabManagerContext()

  const latestCompanyFromRedux = useSelector((state: any) => (recordId ? selectCompanyById(state, recordId) : null))

  const data = latestCompanyFromRedux || company || formData || {}

  const recentRound = data?.[AccountRecordFields.prog__recent_round] || null
  const latestRound = data?.[AccountRecordFields.prog__latest_round] || null

  let roundForPatching = recentRound || latestRound
  if (!roundForPatching) {
    const roundsArray = data?.[AccountRecordFields.prog__rounds] || []
    if (Array.isArray(roundsArray) && roundsArray.length > 0) {
      roundForPatching = roundsArray[0]
    }
  }

  const roundId = roundForPatching?.[RoundEntryFields.rounds__id] || null

  const handleRoundHyperlinkClick = useCallback(
    (module: string, roundIdParam: string, title: string) => {
      if (!roundForPatching || !roundIdParam) return

      dispatch(addRound({ round: roundForPatching }))
      openFormInTab(module, roundIdParam, title)
    },
    [dispatch, openFormInTab, roundForPatching]
  )

  const helpers = useMemo(
    () => ({
      getCurrencyById,
      onRoundHyperlinkClick: handleRoundHyperlinkClick
    }),
    [getCurrencyById, handleRoundHyperlinkClick]
  )

  const roundOverviewFields = useMemo(
    () => getRoundOverviewFields(data, helpers, isReadOnly),
    [data, helpers, isReadOnly, latestCompanyFromRedux]
  )

  const handleRoundPatchSuccess = useMemo(
    () => (patchRoundId: string, updates: Record<string, any>) => {
      if (!recordId) return

      const currentCompanyData = latestCompanyFromRedux || company || formData || {}
      const currentRounds = currentCompanyData[AccountRecordFields.prog__rounds] || []
      const currentRecentRound = currentCompanyData[AccountRecordFields.prog__recent_round] || null
      const currentLatestRound = currentCompanyData[AccountRecordFields.prog__latest_round] || null

      let updatedRound: any = null
      const updatedRounds = currentRounds.map((round: any) => {
        if (round[RoundEntryFields.rounds__id] === patchRoundId) {
          updatedRound = {
            ...round,
            ...updates
          }
          if (updates.pre_money_valuation !== undefined) {
            updatedRound[RoundEntryFields.rounds__pre_money_valuation] = updates.pre_money_valuation
          }
          if (updates.post_money_valuation !== undefined) {
            updatedRound[RoundEntryFields.rounds__post_money_valuation] = updates.post_money_valuation
          }
          if (updates.name !== undefined) {
            updatedRound[RoundEntryFields.rounds__name] = updates.name
          }
          if (updates.round_close_date !== undefined) {
            updatedRound[RoundEntryFields.rounds__round_close_date] = updates.round_close_date
          }
          return updatedRound
        }
        return round
      })

      const companyUpdates: Record<string, any> = {
        [AccountRecordFields.prog__rounds]: updatedRounds
      }

      if (updatedRound) {
        if (currentRecentRound && currentRecentRound[RoundEntryFields.rounds__id] === patchRoundId) {
          companyUpdates[AccountRecordFields.prog__recent_round] = updatedRound
        }

        if (currentLatestRound && currentLatestRound[RoundEntryFields.rounds__id] === patchRoundId) {
          companyUpdates[AccountRecordFields.prog__latest_round] = updatedRound
        }
      }

      Object.keys(updates).forEach((key) => {
        if (key.startsWith('rounds__') || key.startsWith('prog__')) {
          companyUpdates[key] = updates[key]
        }
      })

      dispatch(updateCompanyRecord({ companyId: recordId, updates: companyUpdates }))

      if (onPatchSuccess) {
        onPatchSuccess(recordId, companyUpdates)
      }
    },
    [recordId, latestCompanyFromRedux, company, formData, onPatchSuccess, dispatch]
  )

  return (
    <FormSection
      data-debug='round-overview-section'
      data-component='RoundOverviewSection'
      title='Latest Valuation'
      titleColor='green'
      className='shadow-sm border border-gray-200'
      {...cardProps}
    >
      <div data-debug='round-overview-content'>
        <InputManager
          inputMode='read'
          labelPosition='inline'
          fields={roundOverviewFields}
          onChange={(key, value) => handleFieldChange?.(key, value)}
          enableAutoPatch={!isReadOnly && !!roundId}
          patchId={roundId}
          recordId={roundId || ''}
          onPatchSuccess={handleRoundPatchSuccess}
          className='round-overview-fields'
          fieldWrapping={true}
        />
      </div>
    </FormSection>
  )
}
